import React from 'react';
import { Routes, Route } from 'react-router-dom';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import MarketDataComponent from './components/MarketData';
import MarketInstruments from './components/MarketInstruments';
import Brokers from './components/Brokers';
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import AdminDashboard from './components/AdminDashboard';
import AdminLogin from './components/AdminLogin';
import Header from './components/Header';
import Footer from './components/Footer';

// Fade in animation
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

// Slide up animation
const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Global styles for our professional theme
const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    padding: 0;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    background: linear-gradient(135deg, #0c162d 0%, #122240 50%, #0a192f 100%);
    min-height: 100vh;
    color: #c4cdda;
    overflow-x: hidden;
    background-attachment: fixed;
  }
  
  * {
    box-sizing: border-box;
  }
  
  h1, h2, h3, h4, h5, h6 {
    color: #ffffff;
    font-weight: 600;
  }
  
  p {
    line-height: 1.6;
  }
  
  a {
    text-decoration: none;
  }
`;

// Main container
const AppContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
  position: relative;
  z-index: 1;
  animation: ${fadeIn} 0.5s ease-out;
  
  @media (max-width: 768px) {
    padding: 10px;
  }
`;

// Hero section
const HeroSection = styled.section`
  text-align: center;
  padding: 60px 30px;
  margin-bottom: 50px;
  background: rgba(18, 34, 64, 0.4);
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 194, 255, 0.2);
  position: relative;
  overflow: hidden;
  animation: ${slideUp} 0.8s ease-out;
  
  @media (max-width: 768px) {
    padding: 40px 20px;
    margin-bottom: 30px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 25px;
  background: linear-gradient(90deg, #00c2ff, #64ffda);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.3rem;
  color: #a8b3cf;
  max-width: 700px;
  margin: 0 auto 30px;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

// Section title
const SectionTitle = styled.h2`
  font-size: 2.2rem;
  text-align: center;
  margin: 50px 0 30px;
  color: #ffffff;
  position: relative;
  display: inline-block;
  left: 50%;
  transform: translateX(-50%);
  animation: ${fadeIn} 0.6s ease-out;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, #00c2ff, #64ffda);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin: 40px 0 25px;
  }
`;

// Content grid
const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin: 40px 0;
  animation: ${slideUp} 0.7s ease-out;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
    margin: 30px 0;
  }
`;

// Feature card
const FeatureCard = styled.div`
  background: rgba(18, 34, 64, 0.6);
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 194, 255, 0.2);
  text-align: center;
  animation: ${slideUp} 0.8s ease-out;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 35px rgba(0, 194, 255, 0.4);
    border-color: rgba(0, 194, 255, 0.5);
  }
  
  @media (max-width: 768px) {
    padding: 25px;
  }
`;

const FeatureIcon = styled.div`
  width: 70px;
  height: 70px;
  margin: 0 auto 20px;
  background: linear-gradient(135deg, #00c2ff, #64ffda);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: #0c162d;
  font-size: 1.8rem;
`;

const FeatureTitle = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 15px;
  color: #ffffff;
`;

const FeatureDescription = styled.p`
  color: #a8b3cf;
  line-height: 1.7;
  margin: 0;
`;

// CTA button
const CTAButton = styled.a`
  display: inline-block;
  background: linear-gradient(90deg, #00c2ff, #64ffda);
  color: #0c162d;
  font-weight: 600;
  padding: 15px 40px;
  border-radius: 30px;
  font-size: 1.1rem;
  margin-top: 20px;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(0, 194, 255, 0.4);
  animation: ${fadeIn} 1s ease-out;
  text-decoration: none;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(0, 194, 255, 0.6);
    background: linear-gradient(90deg, #64ffda, #00c2ff);
  }
  
  @media (max-width: 768px) {
    padding: 12px 30px;
    font-size: 1rem;
  }
`;

function App() {
  return (
    <>
      <GlobalStyle />
      <AppContainer>
        <Routes>
          <Route path="/" element={
            <>
              <Header />
              <HeroSection>
                <HeroTitle>UPM Traders</HeroTitle>
                <HeroSubtitle>Your Trusted Platform for Finding Reliable Crypto Trading Brokers in Today's Demanding Market</HeroSubtitle>
                <p style={{ fontSize: '1.1rem', maxWidth: '700px', margin: '20px auto', color: '#a8b3cf', lineHeight: '1.6' }}>
                  Experience financial markets like never before with our revolutionary platform that combines 
                  cutting-edge technology with intuitive design. Trade with clarity, precision, and confidence.
                </p>
                <CTAButton href="/register">Start Trading Today</CTAButton>
              </HeroSection>
              
              <SectionTitle>Why Choose UPM Traders?</SectionTitle>
              <ContentGrid>
                <FeatureCard>
                  <FeatureIcon>📊</FeatureIcon>
                  <FeatureTitle>Real-Time Data</FeatureTitle>
                  <FeatureDescription>
                    Access comprehensive market data across global exchanges with minimal latency and maximum accuracy.
                  </FeatureDescription>
                </FeatureCard>
                
                <FeatureCard>
                  <FeatureIcon>🔒</FeatureIcon>
                  <FeatureTitle>Secure Trading</FeatureTitle>
                  <FeatureDescription>
                    Military-grade encryption and regulated brokers ensure your funds and data are always protected.
                  </FeatureDescription>
                </FeatureCard>
                
                <FeatureCard>
                  <FeatureIcon>🧠</FeatureIcon>
                  <FeatureTitle>Smart Insights</FeatureTitle>
                  <FeatureDescription>
                    Receive personalized market analysis from our team of financial experts and AI algorithms.
                  </FeatureDescription>
                </FeatureCard>
              </ContentGrid>
              
              <SectionTitle>Market Overview</SectionTitle>
              <MarketDataComponent />
              
              <SectionTitle>Popular Instruments</SectionTitle>
              <MarketInstruments />
              
              <SectionTitle>Get Started Today</SectionTitle>
              <HeroSection>
                <p style={{ fontSize: '1.1rem', maxWidth: '700px', margin: '20px auto', color: '#a8b3cf', lineHeight: '1.6' }}>
                  Join thousands of traders who have transformed their trading experience with UPM Traders.
                  Sign up for a free account and discover the difference that professional tools can make.
                </p>
                <CTAButton href="/register">Create Free Account</CTAButton>
              </HeroSection>
              <Footer />
            </>
          } />
          <Route path="/about" element={
            <>
              <Header />
              <SectionTitle>About Us</SectionTitle>
              <HeroSection>
                <p>UPM Traders is dedicated to providing a professional and intuitive experience for financial market data. Our platform offers real-time data with a sophisticated interface designed to enhance trading performance.</p>
                <p>Founded in 2023, we recognized that traditional financial platforms often overwhelm traders with cluttered interfaces and unnecessary complexity. Our mission is to transform how people interact with financial markets by combining advanced technology with clean, intuitive design.</p>
                <p>We believe that informed trading decisions come from clear data visualization and streamlined workflows. Our proprietary approach has helped thousands of traders improve their performance while maintaining focus on what matters most.</p>
              </HeroSection>
              
              <SectionTitle>Who We Are</SectionTitle>
              <HeroSection>
                <p>We are a team of financial experts and technology professionals who believe in creating exceptional trading experiences. Our platform combines advanced financial data with modern design principles to create a unique trading environment.</p>
                <p>Our team includes:</p>
                <ul style={{ textAlign: 'left', maxWidth: '800px', margin: '0 auto', lineHeight: '1.8', color: '#a8b3cf' }}>
                  <li><strong>Former Wall Street Traders:</strong> With decades of combined experience at top financial institutions, our trading experts understand what it takes to succeed in the markets.</li>
                  <li><strong>UX/UI Designers:</strong> Specializing in creating intuitive interfaces that enhance focus and reduce cognitive load.</li>
                  <li><strong>Technology Innovators:</strong> Developers who have created proprietary algorithms for real-time data processing and visualization.</li>
                  <li><strong>Financial Analysts:</strong> Professionals with advanced degrees in finance and economics who provide insightful market analysis.</li>
                </ul>
                <p>Together, we've created a revolutionary approach to financial market interaction that prioritizes trader performance without compromising on functionality.</p>
                <p>Our core values include:</p>
                <ul style={{ textAlign: 'left', maxWidth: '800px', margin: '0 auto', lineHeight: '1.8', color: '#a8b3cf' }}>
                  <li><strong>Transparency:</strong> We believe in clear, honest communication about market conditions and our services.</li>
                  <li><strong>Innovation:</strong> Continuously improving our platform with cutting-edge technology and design.</li>
                  <li><strong>Performance:</strong> Building tools that enhance trading performance and decision-making.</li>
                  <li><strong>Integrity:</strong> Maintaining the highest ethical standards in all our operations.</li>
                </ul>
              </HeroSection>
              
              <SectionTitle>Our Approach</SectionTitle>
              <HeroSection>
                <p>Our unique methodology combines traditional trading techniques with modern technology:</p>
                <ContentGrid>
                  <FeatureCard>
                    <FeatureIcon>📈</FeatureIcon>
                    <FeatureTitle>Data-Driven Analysis</FeatureTitle>
                    <FeatureDescription>
                      We teach traders to approach market analysis with focused attention on key metrics and indicators.
                    </FeatureDescription>
                  </FeatureCard>
                  <FeatureCard>
                    <FeatureIcon>⚡</FeatureIcon>
                    <FeatureTitle>Efficient Execution</FeatureTitle>
                    <FeatureDescription>
                      Our platform minimizes execution time through intuitive design and streamlined workflows.
                    </FeatureDescription>
                  </FeatureCard>
                  <FeatureCard>
                    <FeatureIcon>⚖️</FeatureIcon>
                    <FeatureTitle>Balanced Portfolio</FeatureTitle>
                    <FeatureDescription>
                      We promote diversified strategies that reduce risk while maintaining growth potential.
                    </FeatureDescription>
                  </FeatureCard>
                </ContentGrid>
              </HeroSection>
              <Footer />
            </>
          } />
          <Route path="/brokers" element={
            <>
              <Header />
              <Brokers />
              <Footer />
            </>
          } />
          <Route path="/instruments" element={
            <>
              <Header />
              <SectionTitle>Instruments</SectionTitle>
              <HeroSection>
                <p>UPM Traders provides access to a wide range of financial instruments across global markets. Our carefully curated selection includes the most liquid and popular assets, ensuring you have access to opportunities in all market conditions.</p>
                <p>We focus on instruments with proven liquidity, transparent pricing, and reliable market data. All instruments listed on our platform are available through our partnered brokers with competitive spreads and execution speeds.</p>
              </HeroSection>
              
              <SectionTitle>Market Categories</SectionTitle>
              <HeroSection>
                <ContentGrid>
                  <FeatureCard>
                    <FeatureIcon>💱</FeatureIcon>
                    <FeatureTitle>Forex Majors</FeatureTitle>
                    <FeatureDescription>
                      The most traded currency pairs in the world with tight spreads and high liquidity.
                    </FeatureDescription>
                  </FeatureCard>
                  
                  <FeatureCard>
                    <FeatureIcon>🏛️</FeatureIcon>
                    <FeatureTitle>Stock Indices</FeatureTitle>
                    <FeatureDescription>
                      Major global indices representing market performance across sectors.
                    </FeatureDescription>
                  </FeatureCard>
                  
                  <FeatureCard>
                    <FeatureIcon>💰</FeatureIcon>
                    <FeatureTitle>Commodities</FeatureTitle>
                    <FeatureDescription>
                      Physical assets with intrinsic value for portfolio diversification.
                    </FeatureDescription>
                  </FeatureCard>
                  
                  <FeatureCard>
                    <FeatureIcon>🪙</FeatureIcon>
                    <FeatureTitle>Cryptocurrencies</FeatureTitle>
                    <FeatureDescription>
                      Digital assets revolutionizing finance with 24/7 trading opportunities.
                    </FeatureDescription>
                  </FeatureCard>
                  
                  <FeatureCard>
                    <FeatureIcon>🏢</FeatureIcon>
                    <FeatureTitle>Individual Stocks</FeatureTitle>
                    <FeatureDescription>
                      Leading companies across sectors for targeted equity exposure.
                    </FeatureDescription>
                  </FeatureCard>
                </ContentGrid>
              </HeroSection>
              
              <MarketInstruments />
              <Footer />
            </>
          } />
          <Route path="/services" element={
            <>
              <Header />
              <SectionTitle>Services</SectionTitle>
              <HeroSection>
                <p>Our services include real-time market data, portfolio tracking, risk management tools, and educational resources. We also offer personalized market insights and analysis.</p>
                <p>At UPM Traders, we provide a comprehensive suite of services designed to support traders at every level, from beginners learning the basics to experienced professionals seeking advanced analytical tools.</p>
              </HeroSection>
              
              <SectionTitle>Our Core Services</SectionTitle>
              <HeroSection>
                <ContentGrid>
                  <FeatureCard>
                    <FeatureIcon>📡</FeatureIcon>
                    <FeatureTitle>Real-Time Market Data</FeatureTitle>
                    <FeatureDescription>
                      Access comprehensive, real-time financial data across global markets including:
                      <ul style={{ textAlign: 'left', marginTop: '10px', lineHeight: '1.6' }}>
                        <li>Forex currency pairs</li>
                        <li>Stock indices (S&P 500, Dow Jones, NASDAQ, etc.)</li>
                        <li>Commodities (Gold, Oil, Silver, etc.)</li>
                        <li>Cryptocurrencies (Bitcoin, Ethereum, etc.)</li>
                        <li>Individual stocks from major exchanges</li>
                      </ul>
                    </FeatureDescription>
                  </FeatureCard>
                  
                  <FeatureCard>
                    <FeatureIcon>📋</FeatureIcon>
                    <FeatureTitle>Portfolio Tracking</FeatureTitle>
                    <FeatureDescription>
                      Monitor your investments with our intuitive portfolio management tools:
                      <ul style={{ textAlign: 'left', marginTop: '10px', lineHeight: '1.6' }}>
                        <li>Real-time performance tracking</li>
                        <li>Detailed analytics and reporting</li>
                        <li>Asset allocation visualization</li>
                        <li>Historical performance analysis</li>
                        <li>Customizable alerts and notifications</li>
                      </ul>
                    </FeatureDescription>
                  </FeatureCard>
                  
                  <FeatureCard>
                    <FeatureIcon>🛡️</FeatureIcon>
                    <FeatureTitle>Risk Management Tools</FeatureTitle>
                    <FeatureDescription>
                      Protect your capital and manage risk with our specialized tools:
                      <ul style={{ textAlign: 'left', marginTop: '10px', lineHeight: '1.6' }}>
                        <li>Position sizing calculators</li>
                        <li>Risk/reward analysis</li>
                        <li>Stop-loss and take-profit calculators</li>
                        <li>Portfolio correlation analysis</li>
                        <li>Stress testing scenarios</li>
                      </ul>
                    </FeatureDescription>
                  </FeatureCard>
                </ContentGrid>
              </HeroSection>
              
              <SectionTitle>Educational Resources</SectionTitle>
              <HeroSection>
                <ContentGrid>
                  <FeatureCard>
                    <FeatureIcon>📚</FeatureIcon>
                    <FeatureTitle>Learning Center</FeatureTitle>
                    <FeatureDescription>
                      Comprehensive educational materials for all experience levels:
                      <ul style={{ textAlign: 'left', marginTop: '10px', lineHeight: '1.6' }}>
                        <li>Interactive tutorials on trading fundamentals</li>
                        <li>Advanced strategy guides</li>
                        <li>Market analysis techniques</li>
                        <li>Psychological aspects of trading</li>
                        <li>Video courses and webinars</li>
                      </ul>
                    </FeatureDescription>
                  </FeatureCard>
                  
                  <FeatureCard>
                    <FeatureIcon>🔮</FeatureIcon>
                    <FeatureTitle>Personalized Insights</FeatureTitle>
                    <FeatureDescription>
                      Tailored market analysis and recommendations:
                      <ul style={{ textAlign: 'left', marginTop: '10px', lineHeight: '1.6' }}>
                        <li>Customized market outlooks</li>
                        <li>Asset-specific analysis</li>
                        <li>Risk assessment for your portfolio</li>
                        <li>Opportunity identification</li>
                        <li>Weekly market commentary</li>
                      </ul>
                    </FeatureDescription>
                  </FeatureCard>
                </ContentGrid>
              </HeroSection>
              <Footer />
            </>
          } />
          <Route path="/contact" element={
            <>
              <Header />
              <SectionTitle>Contact Us</SectionTitle>
              <HeroSection>
                <p>If you have any questions or need assistance, please don't hesitate to contact us:</p>
                <p>Email: support@upmtraders.com</p>
                <p>Phone: +1 (800) 555-1234</p>
                <p>Our support team is available Monday through Friday, 9:00 AM to 6:00 PM EST.</p>
              </HeroSection>
              
              <SectionTitle>Our Offices</SectionTitle>
              <HeroSection>
                <ContentGrid>
                  <FeatureCard>
                    <FeatureIcon>🗽</FeatureIcon>
                    <FeatureTitle>New York Headquarters</FeatureTitle>
                    <FeatureDescription>
                      450 Park Avenue, 25th Floor
                      New York, NY 10022
                      Phone: +1 (212) 555-1234
                      Main operations and trading desk
                    </FeatureDescription>
                  </FeatureCard>
                  
                  <FeatureCard>
                    <FeatureIcon>💷</FeatureIcon>
                    <FeatureTitle>London Office</FeatureTitle>
                    <FeatureDescription>
                      25 Cabot Square, 12th Floor
                      London E14 4QA, United Kingdom
                      Phone: +44 20 7123 4567
                      European operations and client services
                    </FeatureDescription>
                  </FeatureCard>
                  
                  <FeatureCard>
                    <FeatureIcon>🗼</FeatureIcon>
                    <FeatureTitle>Tokyo Office</FeatureTitle>
                    <FeatureDescription>
                      1-9-1 Roppongi, 6th Floor
                      Minato-ku, Tokyo 106-0032
                      Phone: +81 3-1234-5678
                      Asian markets coverage and support
                    </FeatureDescription>
                  </FeatureCard>
                </ContentGrid>
              </HeroSection>
              
              <SectionTitle>Support and Resources</SectionTitle>
              <HeroSection>
                <ContentGrid>
                  <FeatureCard>
                    <FeatureIcon>🎧</FeatureIcon>
                    <FeatureTitle>Customer Support</FeatureTitle>
                    <FeatureDescription>
                      Our dedicated support team is here to help with account setup, platform navigation, and technical troubleshooting.
                    </FeatureDescription>
                  </FeatureCard>
                  
                  <FeatureCard>
                    <FeatureIcon>💼</FeatureIcon>
                    <FeatureTitle>Sales Inquiries</FeatureTitle>
                    <FeatureDescription>
                      Interested in our premium services? Contact our sales team for subscription plans and partnership opportunities.
                    </FeatureDescription>
                  </FeatureCard>
                </ContentGrid>
              </HeroSection>
              <Footer />
            </>
          } />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
        </Routes>
      </AppContainer>
    </>
  );
}

export default App;